import type { StepStatus } from "../types";

/**
 * Centralized configuration for all ecommerce pipeline action-required highlights.
 *
 * Each key is a highlight ID used by the SSE pipeline and the store.
 * Keeping every string, label, and description here
 * avoids scattering copy across multiple files and makes it trivial to
 * update messaging without hunting through pipeline code.
 */

// ── Types ──────────────────────────────────────────────────────────────

export interface HighlightConfig {
  /** Unique widget-highlight ID */
  widgetId: string;
  /** Shown in the Actions Required panel */
  comment: string;
  /** Button label */
  actionLabel: string;
  /** Pipeline step index this highlight belongs to */
  stepIndex: number;
  /** 0 = persistent until dismissed, >0 = auto-expire (ms) */
  durationMs: number;
}

// ── Highlight definitions ──────────────────────────────────────────────

/** Step 0 — store / platform connection */
export const STORE_CONNECT: HighlightConfig = {
  widgetId: "storeConnect",
  comment: "Connect your Shopify store to begin product analysis.",
  actionLabel: "Connect Store",
  stepIndex: 0,
  durationMs: 0,
};

export const SHOPIFY_AUTH: HighlightConfig = {
  widgetId: "shopifyAuth",
  comment: "Authorize Shopify access to import your products.",
  actionLabel: "Authorize",
  stepIndex: 0,
  durationMs: 0,
};

/** Step 1 — product discovery gate (metrics still missing) */
export const PRODUCT_METRICS_NEEDED: HighlightConfig = {
  widgetId: "productDiscoveryGate",
  comment: "Add your CPA and delivery cost to unlock accurate unit economics and continue.",
  actionLabel: "Edit Metrics",
  stepIndex: 1,
  durationMs: 0,
};

/** Step 1 — product discovery gate (metrics complete, ready to advance) */
export const PRODUCT_METRICS_READY: HighlightConfig = {
  widgetId: "productDiscoveryGate",
  comment: "Metrics are set — continue to review ad cost estimates.",
  actionLabel: "Continue to CAC Estimates →",
  stepIndex: 1,
  durationMs: 0,
};

export const PRODUCT_GATE_STEP_INDEX = PRODUCT_METRICS_READY.stepIndex;
export const PRODUCT_GATE_NEXT_STEP_INDEX = PRODUCT_GATE_STEP_INDEX + 1;

export const AD_ESTIMATES_STEP_INDEX = 2;
export const AD_ESTIMATES_NEXT_STEP_INDEX = 3; // Plan
export const PLAN_STEP_INDEX = 3;
export const CONTENT_INSPIRATION_STEP_INDEX = PLAN_STEP_INDEX + 1; // 4
export const ADS_OPTIMIZATION_STEP_INDEX = CONTENT_INSPIRATION_STEP_INDEX + 1; // 5

type ProductGateStepState = {
  currentStepIndex: number;
  stepStatuses: Record<number, StepStatus>;
};

/**
 * Shared state transition used when the user confirms the Product Discovery gate.
 * Keeps "confirm & continue" behavior consistent across live SSE events and
 * rehydrated highlights restored from persistence.
 */
export function buildProductGateContinueState<T extends ProductGateStepState>(state: T) {
  return {
    currentStepIndex: PRODUCT_GATE_NEXT_STEP_INDEX,
    stepStatuses: {
      ...state.stepStatuses,
      [PRODUCT_GATE_STEP_INDEX]: "completed" as StepStatus,
      [PRODUCT_GATE_NEXT_STEP_INDEX]: "running" as StepStatus,
    },
  };
}

// ── Helpers ────────────────────────────────────────────────────────────

/** IDs cleared when a store-import begins */
export const STORE_CONNECT_IDS = [STORE_CONNECT.widgetId, SHOPIFY_AUTH.widgetId] as const;

/**
 * Returns true when every product has at least a non-zero COGS,
 * a delivery cost, and a CPA value.
 */
export function areProductMetricsComplete(
  metrics: ReadonlyArray<{ cogs: number; cogsSource?: string; deliveryCost: number; cac: number }>,
): boolean {
  if (metrics.length === 0) return false;
  return metrics.every((m) => m.cogs > 0 && m.deliveryCost > 0 && m.cac > 0);
}

/**
 * Picks the correct highlight config based on current product metrics.
 */
export function getProductGateHighlight(
  metrics: ReadonlyArray<{ cogs: number; cogsSource?: string; deliveryCost: number; cac: number }>,
): HighlightConfig {
  return areProductMetricsComplete(metrics) ? PRODUCT_METRICS_READY : PRODUCT_METRICS_NEEDED;
}
